import { useState } from "react";
import { graphqlClient } from "@/lib/graphql";
import { graphql } from "@/__generated__";
import { useHoneypot } from "@/lib/honeypot";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, RotateCcw } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const REQUEST_REOPEN = graphql(`
  mutation RequestAccountReopen($emailAddress: String!, $reason: String!, $website: String, $formElapsedMs: Int) {
    requestAccountReopen(input: { emailAddress: $emailAddress, reason: $reason, website: $website, formElapsedMs: $formElapsedMs }) {
      success
      errors
    }
  }
`);

// Shown on the signup page when the email belonged to a deleted account.
// Lets the visitor explain why they want back, which notifies support.
export default function AccountReopenForm({ email }: { email: string }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const honeypot = useHoneypot();

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await graphqlClient.request(REQUEST_REOPEN, { emailAddress: email, reason, website: honeypot.website, formElapsedMs: honeypot.elapsedMs() });
      const { success, errors } = res.requestAccountReopen;
      if (!success) {
        toast({ title: "Couldn't send", description: errors.join(" — "), variant: "destructive" });
        return;
      }
      setSent(true);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div className="space-y-3 text-center">
        <MailCheck className="w-10 h-10 mx-auto text-success" />
        <p className="font-semibold text-foreground">Request sent</p>
        <p className="text-sm text-muted-foreground">
          Thanks — our team will review your request to reopen an account for <strong>{email}</strong> and
          get back to you by email within 2 business days.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <input {...honeypot.inputProps} />
      <Callout tone="warning" icon={RotateCcw}>
        <p>
          <strong>{email}</strong> was previously used by an account that was deleted. To reopen an account
          with this email, tell us why below — our team will review and follow up by email.
        </p>
      </Callout>

      <div className="space-y-2">
        <Label htmlFor="reopen-reason">Why would you like to reopen your account?</Label>
        <Textarea
          id="reopen-reason"
          rows={4}
          placeholder="Tell us a bit about why you'd like to come back…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>

      <Button className="w-full" onClick={submit} disabled={reason.trim().length < 10 || submitting}>
        {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Send reopen request
      </Button>
    </div>
  );
}
